// TypeScript schemas for data-driven content (Fase 3).

export interface EnemyStats {
  hp: number;
  speed: number;
  damage: number;
  gems: number;
}

export interface Hitbox {
  w: number;
  h: number;
}

export interface RenderSpec {
  sprite: string;
  anim?: string;
  frames?: string[];
  fps?: number;
}

export interface SpawnSpec {
  rooms?: string[];
  roomTypes?: number[];
  weight: number;
  minDist?: number;
  chance?: number;
}

export interface EnemyDef {
  id: string;
  name: string;
  stats: EnemyStats;
  hitbox: Hitbox;
  render: RenderSpec;
  behavior: string;
  behaviorParams?: Record<string, unknown>;
  spawn?: SpawnSpec;
}

export interface BossPhase {
  hpThreshold: number;
  behavior: string;
  dialog?: string[];
}

export interface BossDef {
  id: string;
  name: string;
  phases: BossPhase[];
  render: RenderSpec;
  arena?: { roomType?: number };
}

export interface AbilityUnlock {
  minExplored?: number;
  minGems?: number;
}

export interface AbilityDef {
  id: string;
  name: string;
  gemCost?: number;
  cooldownMs: number;
  behavior: string;
  unlock?: AbilityUnlock;
  behaviorParams?: Record<string, unknown>;
}

export interface ContentPack {
  enemies: EnemyDef[];
  bosses: BossDef[];
  abilities: AbilityDef[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

export function validateEnemyDef(raw: unknown): EnemyDef {
  if (!isRecord(raw)) throw new Error('enemy: not an object');
  const stats = raw.stats as EnemyStats;
  const render = raw.render as RenderSpec;
  if (typeof raw.id !== 'string') throw new Error('enemy.id required');
  if (!stats || typeof stats.hp !== 'number') throw new Error(`enemy ${raw.id}: stats.hp required`);
  if (!render || typeof render.sprite !== 'string') throw new Error(`enemy ${raw.id}: render.sprite required`);
  if (typeof raw.behavior !== 'string') throw new Error(`enemy ${raw.id}: behavior required`);
  return raw as unknown as EnemyDef;
}

export function validateBossDef(raw: unknown): BossDef {
  if (!isRecord(raw)) throw new Error('boss: not an object');
  if (typeof raw.id !== 'string') throw new Error('boss.id required');
  if (!Array.isArray(raw.phases)) throw new Error(`boss ${raw.id}: phases required`);
  return raw as unknown as BossDef;
}

export function validateAbilityDef(raw: unknown): AbilityDef {
  if (!isRecord(raw)) throw new Error('ability: not an object');
  if (typeof raw.id !== 'string') throw new Error('ability.id required');
  if (typeof raw.behavior !== 'string') throw new Error(`ability ${raw.id}: behavior required`);
  return raw as unknown as AbilityDef;
}
